import React, { useState } from 'react'
import { Typography } from '@mui/material'
import {
  ButtonV2,
  CheckBoxFieldv2,
  ColumnActionWithMenuV2,
  type ColumnMenuOption,
  MainTabsV2,
  SearchBoxComponent,
} from '@labrav/react-components'
import { MarketplaceTemplateTableContainer } from '../MarketplaceTemplateTableContainer/MarketplaceTemplateTableContainer'
import { useStyles } from './AzureMarketplaceTemplateTableContainer.styles'
import { useDispatch, useSelector } from '../../../../store'
import {
  getMarketplaceReferralTemplateSalesforce,
  updateMarketplaceTemplateSectionField,
} from '../../../../modules/marketplaceMappingManager/action'
import type {
  MarketplaceFieldMapping,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../modules/marketplaceMappingManager/types'
import { useParams } from 'react-router-dom'
import { useUserType } from '@labrav/common'
import { CRMTypes } from '../../../../utils/constants'
import { MarketplaceTemplateMetadataTab } from '../MarketplaceTemplateTableContainer/MarketplaceTabConfigurationWrapper/MarketplaceTemplateMetadataTab/MarketplaceTemplateMetadataTab'
import { MarketplaceRecipientSourceTab } from '../MarketplaceTemplateTableContainer/MarketplaceTabConfigurationWrapper/MarketplaceRecipientSourceTab/MarketplaceRecipientSourceTab'

type AzureMarketplaceTemplateTableContainerProps = {
  handleSelectOption: (option: ColumnMenuOption) => void
  menuOptions: ColumnMenuOption[]
  isViewOnly: boolean
}

const tabs = [
  { key: 'fields', label: 'Fields', isError: false },
  { key: 'metadata', label: 'Metadata', isError: false },
  { key: 'recipientSource', label: 'Recipient source', isError: false },
]

export const AzureMarketplaceTemplateTableContainer: React.FC<
  AzureMarketplaceTemplateTableContainerProps
> = ({ handleSelectOption, menuOptions, isViewOnly }) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { accountId = '', templateId = '' } = useParams<{
    accountId?: string
    templateId?: string
  }>()
  const partnerType = useUserType()
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const templateData = useSelector(
    state => state.marketplaceMappingManager?.templateData?.templateData
  )
  const fieldMappings = templateData?.fieldMappings
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState('All')
  const fieldFilter = selectedOption.toLowerCase() as
    | 'all'
    | 'mapped'
    | 'unmapped'
  const handleSearchClick = (value: string) => {
    setSearchNow(!!value.trim())
  }
  const retryTemplateLoad = () => {
    if (!crmId || !accountId || !templateId) return
    dispatch(
      getMarketplaceReferralTemplateSalesforce(
        crmId,
        accountId,
        templateId,
        undefined,
        CRMTypes.hubspot
      )
    )
  }

  return (
    <div className={classes.root} data-testid="azure-marketplace-template">
      {!fieldMappings ? (
        <div className={classes.loadError} role="alert">
          <Typography>
            We couldn’t load this template’s mapping fields.
          </Typography>
          <ButtonV2
            styleType="outlined"
            smallVariant
            disabled={!crmId || !accountId || !templateId}
            onClick={retryTemplateLoad}
          >
            Retry
          </ButtonV2>
        </div>
      ) : (
        <>
          <div className={classes.searchRow}>
            <div className={classes.search}>
              <SearchBoxComponent
                options={['All', 'Mapped', 'Unmapped']}
                handleSearchClick={handleSearchClick}
                loading={false}
                searchNow={searchNow}
                setSearchNow={setSearchNow}
                showCrossMark={showCrossMark}
                setShowCrossMark={setShowCrossMark}
                inputValue={inputValue}
                setInputValue={setInputValue}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                width="400px"
              />
            </div>
            <div
              className={classes.columnAction}
              data-testid="column-action-menu"
            >
              <ColumnActionWithMenuV2
                handleSelectOption={handleSelectOption}
                menuOptions={menuOptions}
                disable={isViewOnly}
              />
            </div>
          </div>
          <MainTabsV2
            tabs={tabs}
            activeTabIndex={activeTabIndex}
            onChange={setActiveTabIndex}
            tabsType="main"
            variant="compactTabs"
          />
          {activeTabIndex === 0 && (
            <MarketplaceTemplateTableContainer
              searchTerm={inputValue}
              section="fields"
              fieldFilter={fieldFilter}
              variant="azure"
              tableHeight="auto"
            />
          )}
          {activeTabIndex === 1 && (
            <MarketplaceTemplateMetadataTab hideObjectField />
          )}
          {activeTabIndex === 2 && (
            <MarketplaceRecipientSourceTab
              crmLabel="HubSpot"
              isAzureHubspot
            />
          )}
          {activeTabIndex === 0 && (
            <div
              className={classes.sections}
              data-testid="azure-mapping-sections"
            >
              <div className={classes.sectionHeader}>
                <span>Section</span>
                <span>Hidden</span>
              </div>
              {[
                { id: 'Labra__Metadata_Hide__c', label: 'Metadata' },
                { id: 'Labra__Stakeholders__c', label: 'Stakeholders' },
              ].map(({ id, label }) => {
                const field = fieldMappings?.[
                  id as keyof MarketplaceTemplatesDataFieldMappings
                ] as MarketplaceFieldMapping | undefined
                return (
                  <div className={classes.sectionRow} key={id}>
                    <span>{label}</span>
                    <CheckBoxFieldv2
                      keyName={`${id}-hidden`}
                      title=""
                      value={!!field?.isHidden}
                      disabled={isViewOnly || !field}
                      onChangeBox={event =>
                        dispatch(
                          updateMarketplaceTemplateSectionField(
                            id as keyof MarketplaceTemplatesDataFieldMappings,
                            { isHidden: event.target.checked }
                          )
                        )
                      }
                    />
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
